// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  Card,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Imports
import CustomChip from "components/mui/chip";
import dayjs from "dayjs";
import { formatUSCurrency } from "components/CurrencyFormat";

const lineItemStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const SubscriptionPaymentReceipt = ({
  open,
  handleClose,
  PaymentData,
}) => {
  const theme = useTheme();

  const prettyDate = (d) =>
    d ? (typeof d === "string" && d.includes("T") ? dayjs(d).format("MMM D, YYYY") : d) : "";

  const DetailContent = () => {
    return (
      <Grid container spacing={5} p={10}>
        {console.log(PaymentData)}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <img src="/images/Logo3.png" height={80} width={80} />
            <Typography variant="h4">Payment Receipt</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                fontWeight="bold"
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#F4F5F6", 
                  px: 3, 
                  py: 1 
                }}
              >
                {/* Show Invoice or Advance/Deposit ref */}
                {PaymentData ? PaymentData.id : ''}
              </Typography>
              <CustomChip
                skin="light"
                color={
                  PaymentData.status === "Completed"
                    ? "success"
                    : PaymentData.status === "Pending"
                      ? "warning"
                      : "error"
                }
                label={PaymentData.status}
                size="small"
                icon={
                  PaymentData.status === "Completed" ? (
                    <Icon icon="tabler:circle-check-filled" />
                  ) : PaymentData.status === "Pending" ? (
                    <Icon icon="tabler:clock-hour-3" />
                  ) : (
                    <Icon icon="tabler:circle-x-filled" />
                  )
                }
              />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Total Amount
              </Typography>
              <Typography variant="h4" color="main.main">
                {formatUSCurrency(PaymentData.amount)}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ border: "1px solid #E1E4EA", borderRadius: 1 }}>
            <Box sx={{ borderBottom: "1px solid #E1E4EA", p: 4 }}>
              <Typography variant="h6" fontWeight="bold">
                Payment Details
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 4,
                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#FAFAFA",
              }}
            >
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Payment ID
                </Typography>
                <Typography variant="body2">
                  {PaymentData ? PaymentData.id : ''}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Payment Date
                </Typography>
                <Typography variant="body2">
                  {PaymentData && PaymentData.createDate
                    ? prettyDate(PaymentData.createDate)
                    : ""}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                 Billing Start Cycle
                </Typography>
                <Typography variant="body2">
                  {prettyDate(PaymentData.billingPeriodStart)}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                 Billing End Cycle
                </Typography>
                <Typography variant="body2">
                  {prettyDate(PaymentData.billingPeriodEnd)}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Invoice Number
                </Typography>
                <Typography variant="body2">{PaymentData.invoiceNumber}</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ border: "1px solid #E1E4EA", borderRadius: 1 }}>
            <Box sx={{ borderBottom: "1px solid #E1E4EA", p: 4 }}>
              <Typography variant="h6" fontWeight="bold">
                Transaction Details
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 4,
                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#FAFAFA",
              }}
            >
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Total Amount
                </Typography>
                <Typography variant="body2">
                  {PaymentData && PaymentData.amount
                    ? formatUSCurrency(PaymentData.amount) : '0.00'
                  }
                </Typography>
              </Box>
              <Divider />
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Paid Amount
                </Typography>
                <Typography variant="body2">
                  {PaymentData && PaymentData.status === 'Completed' ? formatUSCurrency(PaymentData.amount) : '$0.00'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      PaperProps={{
        sx: {
          height: "100%",
          maxHeight: "100vh",
        },
      }}
    >
      <CustomChip
        skin="light"
        color="info"
        label="Preview Mode"
        size="small"
        icon={<Icon icon="tabler:eye" />}
        sx={{
          position: "absolute",
          top: 30,
          right: 42,
          zIndex: 8,
        }}
      />

      <DialogContent>
        <Card>
          <DetailContent />
        </Card>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 4,
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button 
            variant="text" 
            color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
            onClick={() => {
              handleClose();
            }}
          >
            Close
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="text"
            color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
            onClick={() => {
              handleClose();
            }}
          >
            Back to List View
          </Button>
          <Button variant="contained" color="main"
            onClick={() => {
              handleClose();
            }}
          >
            Send Receipt
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionPaymentReceipt;
